//Built-in libraries

import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
//Third party libraries
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import type { IGetUsersServiceParams } from '@/api/user';
//Custom components
import SectionHeading from '@/components/SectionHeading';
import TableFooter from '@/components/Table/TableFooter';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import { FilterBadge } from '@/components/UI/FilterBadge';
import Modal from '@/components/UI/Modal';
import SimpleDropDown, { DropDownItem } from '@/components/UI/SimpleDropDown';
import NewUserForm from '@/components/Users/NewUserForm';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { useUsers } from '@/hooks/ReactQueryHooks/useUsersQuery';
//Services
import { authSelector } from '@/store';
import type { User } from '@/types/User';
//Assets and Miscellaneous
import { capitalize, daysCount, getInitials, isObjectEmpty } from '@/utils';
import UserFilterForm from './Components/UserFilterForm';

//Component Begin
export const Users = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentUserData } = useSelector(authSelector);
  const [perPage, setPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const today = new Date().toISOString();
  const [sortOrder, setSortOrder] = useState<{
    order: undefined | string;
    orderBy: undefined | string;
  }>({
    order: undefined,
    orderBy: undefined,
  });

  const [appliedFilters, setAppliedFilters] = useState<{
    [key: string]: string;
  }>({});
  const [showFilterModal, setShowFilterModal] = useState(false);

  const fetchUsers = useUsers({
    params: { perPage, page, ...sortOrder, ...appliedFilters },
  });
  const users = fetchUsers.data?.data;
  //Substracting 1 from the total because
  //current user account is not visible in this list
  const totalPage = fetchUsers.data ? Math.ceil((fetchUsers.data?.meta.total - 1) / perPage) : 5;

  const getAccountTitles = useOrgTitles({});
  const accountTitles = getAccountTitles?.data?.data;

  const onPageChangeHandler = (direction: string) => {
    if (direction === 'prev') {
      setPage((prevState) => (prevState > 1 ? page - 1 : 1));
    } else if (direction === 'next') {
      setPage((prevState) => (prevState === totalPage ? totalPage : page + 1));
    }
  };

  const sortHandler = ({ orderBy, order }: { orderBy: string; order: string }) => {
    setSortOrder({
      order,
      orderBy,
    });
  };

  const removeFilterHandler = (key: string) => {
    const updatedFilters = { ...appliedFilters };
    delete updatedFilters[key];
    setAppliedFilters(updatedFilters);
  };

  const applyFilterHandler = (filterData: Partial<IGetUsersServiceParams>) => {
    setPage(1);
    setAppliedFilters(filterData);
    setShowFilterModal(false);
  };

  const getTitleById = (id: number) => {
    return accountTitles?.find((title) => title.id === id);
  };

  return (
    <div className='flex flex-col w-full mx-auto'>
      {showNewUserForm && (
        <Modal>
          <NewUserForm formModalHandler={() => setShowNewUserForm((prevState) => !prevState)} />
        </Modal>
      )}
      {showFilterModal && (
        <UserFilterForm
          handleModal={() => {
            setShowFilterModal(false);
          }}
          applyFilters={applyFilterHandler}
          defaultValues={appliedFilters}
        />
      )}
      <div className='align-middle inline-block min-w-full text-sm'>
        <SectionHeading
          title='Users'
          titleStyle='border-l-4 border-primaryColor-normal py-2 px-4 font-medium text-xl'
          buttonConfig={{
            label: 'Add New user',
            onClick: () => setShowNewUserForm(true),
            permission: {
              of: 'INVITE_USER',
            },
          }}
        />
        <section className='border-t'>
          <div className='flex m-4 gap-4 items-center'>
            <button
              className='p-2 rounded-md bg-gray-100 flex gap-1 items-center hover:bg-neutralColor-light cursor-pointer text-xs font-medium transition duration-300 ease-in-out'
              onClick={() => setShowFilterModal(true)}
            >
              <PlusIcon className='h-4 w-4' />
              <span>Add Filter</span>
            </button>
            {!isObjectEmpty(appliedFilters) && (
              <div className='my-2 flex gap-2'>
                {Object.keys(appliedFilters)?.map((key, index) => {
                  return (
                    <FilterBadge
                      key={index}
                      title={`${key === 'title_id' ? 'Title' : capitalize(key)}: ${
                        key === 'title_id' ? getTitleById(parseInt(appliedFilters[key], 10))?.name : appliedFilters[key]
                      }`}
                      onClear={() => removeFilterHandler(key)}
                    />
                  );
                })}
              </div>
            )}
          </div>
          {!isObjectEmpty(appliedFilters) && <div className='p-4 -mt-4'>{fetchUsers?.data?.meta?.total} users found</div>}
          <table className='w-full'>
            <thead className='bg-gray-100'>
              <tr>
                {['Name', 'Title', 'Status', 'Role'].map((head) => (
                  <th
                    key={head}
                    scope='col'
                    className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'
                  >
                    <div className='flex gap-2 items-center relative'>
                      {head}
                      {head !== 'Status' && (
                        <SimpleDropDown
                          menuButtonClassName='flex text-sm items-start'
                          menuItemClassName='left-0 capitalize font-medium'
                          toggle={<ChevronDownIcon className='h-4 w-4' />}
                        >
                          <p className='px-3 pt-2'>Sort</p>
                          <DropDownItem
                            id='orderByAsc'
                            onClick={() =>
                              sortHandler({
                                orderBy: head.toLowerCase(),
                                order: 'asc',
                              })
                            }
                          >
                            <span>Ascending</span>
                          </DropDownItem>
                          <DropDownItem
                            id='orderByDesc'
                            onClick={() =>
                              sortHandler({
                                orderBy: head.toLowerCase(),
                                order: 'desc',
                              })
                            }
                          >
                            <span>Descending</span>
                          </DropDownItem>
                        </SimpleDropDown>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            {fetchUsers.isFetching && (
              <tbody className='bg-white divide-y divide-gray-200 animate-pulse'>
                {[0, 1, 2, 3, 4, 5].map((num) => (
                  <tr key={num}>
                    <td className='p-6'>
                      <div className='flex gap-2 items-center'>
                        <span className='h-10 w-10 rounded-full bg-neutralColor-light block'></span>
                        <div className='space-y-2'>
                          <span className='h-2 rounded-md bg-neutralColor-light w-32 block'></span>
                          <span className='h-2 rounded-md bg-neutralColor-light w-20 block'></span>
                        </div>
                      </div>
                    </td>
                    <td className='p-6'>
                      <span className='h-2 rounded-md bg-neutralColor-light w-32 block'></span>
                    </td>
                    <td className='p-6'>
                      <span className='h-6 rounded-full bg-neutralColor-light w-20 block'></span>
                    </td>
                    <td className='p-6'>
                      <span className='h-2 rounded-md bg-neutralColor-light w-32 block'></span>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
            {!fetchUsers.isFetching && (
              <tbody className='divide-y w-full'>
                {fetchUsers.isSuccess &&
                  users?.map((user: User) => {
                    if (user.id !== currentUserData.id) {
                      return (
                        <tr
                          key={user.id}
                          className='hover:bg-green-50 cursor-pointer'
                          onClick={() => navigate(`${location.pathname}/${user.id}`)}
                        >
                          <td className='px-6 py-4 whitespace-nowrap'>
                            <div className='flex items-center'>
                              <ImageCircularAvatar imageUrl={user.profileImageUrl} fallbackText={getInitials(user.name)} />
                              <div className='ml-4'>
                                <div className='text-sm font-semibold text-gray-900'>
                                  {user.name}
                                  {daysCount(today, user.createdAt.toString()) < 3 && (
                                    <span className='text-xs px-2 text-red-400 rounded-full font-semibold'>New</span>
                                  )}
                                </div>
                                <div className='text-xs text-neutralColor-normal'>{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap'>
                            <div className='text-sm text-neutralColor-normal'>{user.title}</div>
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap'>
                            {user.active && !user.firstInvite && <Badge badgeType='primary' text='Active' />}
                            {user.firstInvite && <Badge badgeType='secondary' text='Invited' />}
                            {!user.active && !user.firstInvite && <Badge badgeType='tertiary' text='Suspended' />}
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>{user.role}</td>
                        </tr>
                      );
                    }
                    return null;
                  })}
              </tbody>
            )}
          </table>
          <TableFooter
            currentPage={page}
            totalPage={totalPage}
            perPageHandler={(v) => setPerPage(+v.target.value)}
            pageChangeHandler={onPageChangeHandler}
          />
        </section>
      </div>
    </div>
  );
};
